import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import './OurMenu.css';
import Footer from '../Footer';
import { db, functions } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Default menu — used until you save your own from the admin page.
// You no longer need to edit this; manage the live menu at /admin-menu.
export const DEFAULT_MENU = [
    {
        category: 'Espresso',
        items: [
            { name: 'Espresso', desc: 'A concentrated, full-bodied shot', price: '$3.50' },
            { name: 'Americano', desc: 'Espresso with hot water', price: '$4.00' },
            { name: 'Latte', desc: 'Espresso with steamed milk', price: '$5.00' },
            { name: 'Cappuccino', desc: 'Espresso, steamed milk & foam', price: '$5.00' },
            { name: 'Mocha', desc: 'Espresso, chocolate & steamed milk', price: '$5.50' },
        ],
    },
    {
        category: 'Brewed Coffee',
        items: [
            { name: 'Drip Coffee', desc: 'Freshly roasted single origin', price: '$3.50' },
            { name: 'Pour Over', desc: 'Hand-brewed, rotating origins', price: '$5.50' },
            { name: 'Cold Brew', desc: 'Slow-steeped 18 hours, smooth & bold', price: '$5.00' },
        ],
    },
    {
        category: 'Specialty',
        items: [
            { name: 'Panama Geisha Pour Over', desc: 'Floral, tea-like, our finest lot', price: '$12.00' },
            { name: 'Yemen Mokha Matari', desc: 'Rich, winey, deep chocolate notes', price: '$9.00' },
            { name: 'Seasonal Signature Drink', desc: 'Ask your barista', price: '' },
        ],
    },
    {
        category: 'Retail Beans',
        items: [
            { name: 'Single Origin — 12 oz', desc: 'Roasted to order, rotating origins', price: 'from $18' },
            { name: 'Signature Blend — 12 oz', desc: 'Our house roast', price: '$16' },
            { name: 'Gift Box', desc: 'Curated trio of origins', price: '$54' },
        ],
    },
];

// Parse "$5.00" / "$16" → cents. Returns null for "from $18", "", etc.
function priceToCents(str) {
    if (typeof str !== 'string') return null;
    const trimmed = str.trim().replace(/^\$/, '');
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
    return Math.round(parseFloat(trimmed) * 100);
}

const isHotOnly = (name) => /cappuccino|espresso/i.test(name);
const isMilkDrink = (name) => /latte|cappuccino|mocha/i.test(name);

// Per-item thumbnail. Match on the item's exact name (case/space-insensitive).
const ITEM_IMAGES = {
    'peru washed gesha': '/images/Peru Washed Gesha.jpeg',
    'colombia washed sidra': '/images/Colombia Washed Sidra.jpeg',
    'ethiopia guji washed heirloom': '/images/Ethiopia Guji Washed Heirloom.jpeg',
};
const itemImageFor = (name) =>
    ITEM_IMAGES[String(name || '').toLowerCase().replace(/\s+/g, ' ').trim()] || null;
const money = (cents) => `$${(cents / 100).toFixed(2)}`;

function OurMenu() {
    const navigate = useNavigate();
    const [menu, setMenu] = useState(DEFAULT_MENU);
    const [order, setOrder] = useState([]); // { name, temp, unit, quantity }
    const [showCheckout, setShowCheckout] = useState(false);
    const [milkChoice, setMilkChoice] = useState({}); // { [drinkName]: 'Whole' | 'Oat' }
    const [milkOptions, setMilkOptions] = useState([
        { name: 'Whole', available: true },
        { name: 'Oat', available: true },
    ]);
    const [custName, setCustName] = useState('');
    const [custEmail, setCustEmail] = useState('');
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = 'Our Menu — Order Coffee & Specialty Drinks | OFFF THE ROAST';
    }, []);

    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, 'siteContent', 'menu'),
            (snap) => {
                const data = snap.data();
                if (data && Array.isArray(data.sections) && data.sections.length > 0) {
                    setMenu(data.sections);
                } else {
                    setMenu(DEFAULT_MENU);
                }
                if (data && Array.isArray(data.milkOptions) && data.milkOptions.length > 0) {
                    setMilkOptions(
                        data.milkOptions
                            .filter((m) => m && m.name)
                            .map((m) => ({ name: m.name, available: m.available !== false }))
                    );
                } else {
                    setMilkOptions([
                        { name: 'Whole', available: true },
                        { name: 'Oat', available: true },
                    ]);
                }
            },
            () => setMenu(DEFAULT_MENU)
        );
        return () => unsub();
    }, []);

    const addToOrder = (name, unit, temp, milk = null) => {
        setOrder((prev) => {
            const i = prev.findIndex(
                (x) => x.name === name && x.temp === temp && (x.milk || null) === (milk || null)
            );
            if (i >= 0) {
                const next = [...prev];
                next[i] = { ...next[i], quantity: next[i].quantity + 1 };
                return next;
            }
            return [...prev, { name, temp, milk, unit, quantity: 1 }];
        });
    };

    const changeQty = (idx, delta) => {
        setOrder((prev) => {
            const next = [...prev];
            const q = next[idx].quantity + delta;
            if (q <= 0) {
                next.splice(idx, 1);
            } else {
                next[idx] = { ...next[idx], quantity: q };
            }
            return next;
        });
    };

    const orderCount = order.reduce((s, x) => s + x.quantity, 0);
    const orderTotal = order.reduce((s, x) => s + x.unit * x.quantity, 0);

    const validateForm = () => {
        if (!custName.trim()) return 'Please enter your name.';
        if (custEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(custEmail.trim())) {
            return 'Please enter a valid email, or leave it blank.';
        }
        if (order.length === 0) return 'Your order is empty.';
        return '';
    };

    const itemsPayload = () =>
        order.map((x) => ({ name: x.name, temp: x.temp, milk: x.milk || undefined, quantity: x.quantity }));

    const handlePayOnline = async () => {
        const err = validateForm();
        if (err) { setFormError(err); return; }
        setFormError('');
        setSubmitting(true);
        try {
            const fn = httpsCallable(functions, 'createDrinkCheckoutSession');
            const res = await fn({
                customerName: custName.trim(),
                customerEmail: custEmail.trim(),
                items: itemsPayload(),
                origin: window.location.origin,
            });
            if (res.data && res.data.url) {
                window.location.href = res.data.url;
            } else {
                throw new Error('No checkout URL');
            }
        } catch (e) {
            console.error(e);
            setFormError('Could not start online payment. Please try again.');
            setSubmitting(false);
        }
    };

    const handlePayInPerson = async () => {
        const err = validateForm();
        if (err) { setFormError(err); return; }
        setFormError('');
        setSubmitting(true);
        try {
            const fn = httpsCallable(functions, 'submitDrinkOrder');
            const res = await fn({
                customerName: custName.trim(),
                customerEmail: custEmail.trim(),
                items: itemsPayload(),
            });
            const oid = res.data && res.data.orderId;
            navigate(oid ? `/order-received?orderId=${encodeURIComponent(oid)}` : '/order-received');
        } catch (e) {
            console.error(e);
            setFormError('Could not place your order. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <>
            <div
                className='ourmenu-hero'
                style={{
                    backgroundImage: `linear-gradient(rgba(20,14,10,0.2), rgba(20,14,10,0.2)), url(${process.env.PUBLIC_URL}/images/drink.jpg)`,
                }}
            >
            </div>

            <div className='main-content'>
                <div className='ourmenu-container'>
                    <div className='ourmenu-intro'>
                        <h1>OUR MENU</h1>
                        <p>Crafted from beans we roast ourselves</p>
                    </div>
                    {menu.map((section) => {
                        const visibleItems = (section.items || []).filter((it) => it.hidden !== true);
                        if (visibleItems.length === 0) return null;
                        return (
                        <section className='ourmenu-section' key={section.category}>
                            <h2 className='ourmenu-category'>{section.category}</h2>
                            {section.note && (
                                <p className='ourmenu-note'>{section.note}</p>
                            )}
                            <ul className='ourmenu-list'>
                                {visibleItems.map((item) => {
                                    const cents = priceToCents(item.price);
                                    const hotOnly = isHotOnly(item.name);
                                    const availMilkNames = milkOptions
                                        .filter((m) => m.available)
                                        .map((m) => m.name);
                                    const milkDrink = isMilkDrink(item.name) && milkOptions.length > 0;
                                    const chosen = milkChoice[item.name];
                                    const selMilk = availMilkNames.includes(chosen)
                                        ? chosen
                                        : (availMilkNames[0] || (milkOptions[0] && milkOptions[0].name));
                                    const itemImg = itemImageFor(item.name);
                                    return (
                                        <li
                                            className={`ourmenu-item${itemImg ? ' ourmenu-item--with-image' : ''}`}
                                            key={item.name}
                                        >
                                            {itemImg && (
                                                <img
                                                    src={`${process.env.PUBLIC_URL}${itemImg}`}
                                                    alt={item.name}
                                                    className='ourmenu-item-img'
                                                />
                                            )}
                                            <div className='ourmenu-item-head'>
                                                <span className='ourmenu-item-name'>{item.name}</span>
                                                <span className='ourmenu-item-dots' />
                                                {item.price && (
                                                    <span className='ourmenu-item-price'>{item.price}</span>
                                                )}
                                            </div>
                                            {item.desc && (
                                                <p className='ourmenu-item-desc'>{item.desc}</p>
                                            )}
                                            {cents !== null && (
                                                <div className='ourmenu-add'>
                                                    {milkDrink && (
                                                        <div className='ourmenu-milk'>
                                                            <span className='ourmenu-milk-label'>Milk</span>
                                                            <select
                                                                className='ourmenu-milk-select'
                                                                value={selMilk}
                                                                onChange={(e) =>
                                                                    setMilkChoice((prev) => ({ ...prev, [item.name]: e.target.value }))
                                                                }
                                                            >
                                                                {milkOptions.map((m) => (
                                                                    <option
                                                                        key={m.name}
                                                                        value={m.name}
                                                                        disabled={!m.available}
                                                                        style={!m.available ? { color: '#b0b0b0' } : undefined}
                                                                    >
                                                                        {m.name}{!m.available ? ' — unavailable' : ''}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                    <div className='ourmenu-add-row'>
                                                        {hotOnly ? (
                                                            <button
                                                                className='ourmenu-add-btn'
                                                                onClick={() => addToOrder(item.name, cents, 'Hot', milkDrink ? selMilk : null)}
                                                            >
                                                                + Add (Hot only)
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    className='ourmenu-add-btn'
                                                                    onClick={() => addToOrder(item.name, cents, 'Hot', milkDrink ? selMilk : null)}
                                                                >
                                                                    + Add Hot
                                                                </button>
                                                                <button
                                                                    className='ourmenu-add-btn ourmenu-add-btn--iced'
                                                                    onClick={() => addToOrder(item.name, cents, 'Iced', milkDrink ? selMilk : null)}
                                                                >
                                                                    + Add Iced
                                                                </button>
                                                                {/americano/i.test(item.name) && (
                                                                    <button
                                                                        className='ourmenu-add-btn ourmenu-add-btn--nitro'
                                                                        onClick={() => addToOrder(item.name, cents, 'Nitro', null)}
                                                                    >
                                                                        + Add Nitro
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                        );
                    })}
                </div>
            </div>

            {/* Sticky order bar */}
            {orderCount > 0 && !showCheckout && (
                <div className='ourmenu-bar'>
                    <div className='ourmenu-bar__info'>
                        <strong>{orderCount}</strong> {orderCount === 1 ? 'drink' : 'drinks'} · {money(orderTotal)}
                    </div>
                    <button className='ourmenu-bar__btn' onClick={() => setShowCheckout(true)}>
                        REVIEW &amp; CHECK OUT
                    </button>
                </div>
            )}

            {/* Checkout modal */}
            {showCheckout && (
                <div className='ourmenu-modal' onClick={() => !submitting && setShowCheckout(false)}>
                    <div className='ourmenu-modal__panel' onClick={(e) => e.stopPropagation()}>
                        <button
                            className='ourmenu-modal__close'
                            onClick={() => !submitting && setShowCheckout(false)}
                            aria-label='Close'
                        >
                            ✕
                        </button>
                        <h2 className='ourmenu-modal__title'>Your Order</h2>

                        {order.length === 0 ? (
                            <p className='ourmenu-modal__empty'>Your order is empty.</p>
                        ) : (
                            <div className='ourmenu-modal__items'>
                                {order.map((x, idx) => (
                                    <div className='ourmenu-oitem' key={`${x.name}-${x.temp}-${x.milk || ''}`}>
                                        <div className='ourmenu-oitem__name'>
                                            {x.name} <span className='ourmenu-oitem__temp'>{x.temp}</span>
                                            {x.milk && <span className='ourmenu-oitem__temp'>{x.milk} milk</span>}
                                        </div>
                                        <div className='ourmenu-oitem__qty'>
                                            <button onClick={() => changeQty(idx, -1)}>−</button>
                                            <span>{x.quantity}</span>
                                            <button onClick={() => changeQty(idx, 1)}>+</button>
                                        </div>
                                        <div className='ourmenu-oitem__price'>{money(x.unit * x.quantity)}</div>
                                    </div>
                                ))}
                                <div className='ourmenu-modal__total'>
                                    <span>Total</span>
                                    <span>{money(orderTotal)}</span>
                                </div>
                            </div>
                        )}

                        <div className='ourmenu-form'>
                            <label>Name</label>
                            <input
                                type='text'
                                value={custName}
                                onChange={(e) => setCustName(e.target.value)}
                                placeholder='Your name'
                                disabled={submitting}
                            />
                            <label>Email (optional)</label>
                            <input
                                type='email'
                                value={custEmail}
                                onChange={(e) => setCustEmail(e.target.value)}
                                placeholder='you@example.com'
                                disabled={submitting}
                            />
                            {formError && <p className='ourmenu-form__error'>{formError}</p>}

                            <div className='ourmenu-pay'>
                                <button
                                    className='ourmenu-pay__online'
                                    onClick={handlePayOnline}
                                    disabled={submitting || order.length === 0}
                                >
                                    {submitting ? 'PLEASE WAIT…' : `PAY ONLINE — ${money(orderTotal)}`}
                                </button>
                                <button
                                    className='ourmenu-pay__person'
                                    onClick={handlePayInPerson}
                                    disabled={submitting || order.length === 0}
                                >
                                    {submitting ? 'PLEASE WAIT…' : 'PAY IN PERSON (CASH OR CARD)'}
                                </button>
                            </div>
                            <p className='ourmenu-pay__note'>
                                Pay online by card now, or place the order and pay at pickup with
                                cash or credit card.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <Footer />
        </>
    );
}

export default OurMenu;
